import React from "react";
import axios from "axios";
import { AlertColor, Button, IconButton, InputAdornment, MenuItem, TextField, useMediaQuery} from "@mui/material";
import { Print, Search } from "@mui/icons-material";
import { DataGridPro, GridColumns, GridRowId, GridToolbarContainer } from '@mui/x-data-grid-pro';
import Title from "../../component/Title";
import { globalTheme } from "../../../common/theme";
import { useConstructor } from "../../../common/UseConstructor";
import { CustomTablePagination } from "../../component/CustomPagination";
import { useAppSelector } from "../../../common/hooks";
import { RootState } from "../../../common/Store";
import { CommonProps } from "../../component/MainContents";
import { renewToken } from "../../../services/AuthService";
import { SnackBarMessage, SnackBarMessageProps } from "../../component/SnackBarMessage";
import { OpenMode } from "../../../common/Enums";
import { Customer, CustomerListItem } from "./CustomerAccessories";
import { defaultPaymentTerm, Languages } from "../../../common/Constants";
import { CustomerEnvelopViewer, CustomerEnvelopViewerHandle } from "./CustomerEnvelopViewer";

export function CustomerEnvelops(props: CommonProps) {
	const [loading, setLoading] = React.useState(true);
	const [hasError, setHasError] = React.useState(false);

    const isDebug = useAppSelector((state: RootState) => state.menuToggle.debugMode);

    const viewerModalRef = React.useRef<CustomerEnvelopViewerHandle>(null);

    const style = { ...globalTheme() };

    const tinyScreen = useMediaQuery('(max-width:1366px)');
    const smallScreen = useMediaQuery('(max-width:1920px)');
    const normalScreen = useMediaQuery('(max-width:2560px)');

	//Grid columns
	const columns: GridColumns = [
		{ field: 'companyNameEng', headerName: 'Company Name', flex: 4, type: 'string' },
		{ field: 'companyNameChi', headerName: '中国語', flex: 4, type: 'string' },
		{ field: 'companyNameJpn', headerName: '日本語', flex: 4, type: 'string' }
	];

    const [pageSize, setPageSize] = React.useState(15);

    const [searchText, setSearchText] = React.useState("");
    const [selectedLanguageId, setSelectedLanguageId] = React.useState<number>(1);

	const [gridData, setGridData] = React.useState<CustomerListItem[]>([]);
    const [selectionModel, setSelectionModel] = React.useState<GridRowId[]>([]);
    const selectedData = React.useRef<CustomerListItem[]>([]);

	useConstructor(() => Initialise())
    function Initialise() {
        setLoading(true);
		renewToken();

		LoadData(searchText);
    }

    //Dialogs and snacks
    const [snackSettings, setSnackSettings] =
      React.useState<SnackBarMessageProps>({
        open: false,
        message: "",
        subMessage: "",
        severity: undefined,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });

    function showSnackBar(message: string, subMessage: string, colour?: AlertColor) {
      setSnackSettings({
        open: true,
        message: message,
        subMessage: subMessage,
        severity: colour,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });
    }

	function LoadData(text: string) {
		setLoading(true);

		let searchText = text == '' ? 'ANY' : text;
		const params = { searchString: searchText };
		axios.get<CustomerListItem[]>('Api/V1/Customer/Search', {
			params,
			headers: {
			  'Content-Encoding': 'gzip'
			}
		  })
			.then((result) => {
				setGridData(result.data);
				setLoading(false);
			},
			  (error) => {
				setLoading(false);
				console.log(error);
				showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			}
			)
			.catch((error) => {
			  setLoading(false);
			  console.log(error);
			  showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			});
	}


	function CustomToolbar() {
		const [inputText, setInputText] = React.useState(searchText);
		return (
			<GridToolbarContainer style={{ display: 'flex' }}>
				<TextField className={style.ModalContentFieldHalfWidth}
					style={{ minWidth: '150px', width: '100%' }}
					label="Customer name"
					size="small"
					value={inputText}
					onChange={(e) => setInputText(e.target.value)}
					onKeyDown={(e) => KeyPressed(e.key, inputText)}
					InputProps={{
					endAdornment: (
						<InputAdornment position="end">
							<IconButton onClick={() => {
									setSearchText(inputText);
									LoadData(inputText);
								}}>
								<Search />
							</IconButton>
						</InputAdornment>
					),
					}}            
				/>
				<TextField 
					value={selectedLanguageId}
					onChange={(e) => setSelectedLanguageId(parseInt(e.target.value))}
					style={{ minWidth: tinyScreen ? "80px" : (smallScreen ? "110px" : "140px"), width: tinyScreen ? "80px" : (smallScreen ? "110px" : "140px") }}
					select
					label="Language"
					size="small"
				>
					{Languages.filter(x => x.id < 4).map((item) => (
						<MenuItem key={item.id} value={item.id}>{item.value}</MenuItem>
					))}
				</TextField>
				<div style={{ width: "10px", minWidth: "10px" }}></div>
				<Button variant="contained" color="secondary" style={{ margin: "2px" }} startIcon={<Print />} onClick={() => PreviewEnvelops()}>Preview</Button>
			</GridToolbarContainer>
		);
	}

    function KeyPressed(key: string, inputText: string) {
		if(key === 'Enter'){
		  setSearchText(inputText)
		  LoadData(inputText);
		}      
	}
  
	function PreviewEnvelops() {
		if (selectedData.current.length > 0)
		{
			let ids: string = "";
			selectedData.current.forEach(x => {
				ids = ids + x.customerId.toString() + ",";
			});
			viewerModalRef.current?.Initialise(ids.substring(0,ids.length-1), selectedLanguageId);
		}
	}

    return (
        <div className={style.PageMain}>
			<Title pageTitle="Envelop Printing" />
			<div className={style.PageContent}>
				<DataGridPro
					getRowId={(row) => row.customerId}
					rows={gridData}
					columns={columns}
					pageSize={pageSize}
					onPageSizeChange={(pageSize) => setPageSize(pageSize)}
					pagination
					rowsPerPageOptions={[15, 30, 50, 100]}
					loading={loading}
					components={{
						Toolbar: CustomToolbar,
						Footer: CustomTablePagination
					}}
					componentsProps={{
						footer: { rowsPerPageOptions: [15, 30, 50, 100] }
					}}
					checkboxSelection
					selectionModel = {selectionModel}
					onSelectionModelChange={(selectionModel) => {
					  setSelectionModel(selectionModel)
					  const ids = new Set(selectionModel);
					  const selectedRows = gridData.filter((row) => ids.has(row.customerId));
					  selectedData.current = selectedRows;
					}}
					headerHeight={tinyScreen ? 30 : (smallScreen ? 34 : 38)}
					rowHeight={tinyScreen ? 22 : (smallScreen ? 28 : 34)}
				/>
			</div>

			<CustomerEnvelopViewer ref={viewerModalRef} onClose={() => { }} />

			<SnackBarMessage {...snackSettings} />

        </div>
    );
}

